import {
  createContext, useState, useEffect, useContext, useCallback, useRef,
} from 'react';
import { auth, fetchUserInfo, storage, firestore, now } from '../../../utils/firebase';
import { createOrFetchUser } from '../../trashie/utils/service';
import { updateCrossDomainCookie } from '../../trashie/utils/crossDomainCookie';
import { markTokenAsRefreshed } from '../../../api/crud';
import { clearGa4UserId } from '../../trashie/scripts/gaUserId';
import safeStorage from '../../trashie/utils/localStorage';

const FirebaseAuthContext = createContext(undefined);

const identifyImpactUser = (userAuth) => {
  if (typeof window === 'undefined' || typeof window.ire !== 'function' || !userAuth?.email) {
    return;
  }

  window.ire('identify', {
    customerId: userAuth.uid || '',
    customerEmail: userAuth.email,
    customProfileId: userAuth.uid || '',
  });
};

export function useFirebaseAuth() {
  const context = useContext(FirebaseAuthContext);
  if (context === undefined) {
    throw new Error(
      'useFirebaseAuth must be used within a FirebaseProvider',
    );
  }
  const contexty = {
    isSignedIn: !!context.user,
    user: context.user,
    isLoading: context.isLoading,
    fbKey: context.fbKey,
    userData: context.userData,
    reloadUserData: context.handleSetUserData,
    storage,
    firestore,
    now,
    signOut: context.signOut,
    pauseAuthListener: context.pauseAuthListener,
    resumeAuthListener: context.resumeAuthListener,
    verifyEmailError: context.verifyEmailError,
    clearVerifyEmailError: context.clearVerifyEmailError,
  };

  return contexty;
}

export const FirebaseProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [verifyEmailError, setVerifyEmailError] = useState(null);
  const authListenerPausedRef = useRef(false);
  const callbackIdRef = useRef(0);

  const pauseAuthListener = useCallback(() => {
    authListenerPausedRef.current = true;
  }, []);

  const resumeAuthListener = useCallback(() => {
    authListenerPausedRef.current = false;
  }, []);

  const fetchUserData = useCallback(async (userAuth) => {
    const userInfo = await fetchUserInfo(userAuth.uid);
    const signUpMethod = safeStorage.getItem('signUp_method');
    const optIn = safeStorage.getItem('optIn');

    let pendingSignupData = {};
    try {
      const raw = safeStorage.getItem('pending_signup_data');
      if (raw) {
        pendingSignupData = JSON.parse(raw);
      }
    } catch {
      // ignore malformed data
    }

    const { data: dbData, error: dbError } = await createOrFetchUser(
      userAuth.email,
      signUpMethod,
      optIn,
      pendingSignupData,
    );

    safeStorage.removeItem('signUp_method');
    safeStorage.removeItem('optIn');
    safeStorage.removeItem('pending_signup_data');

    let data = {};
    let error;
    if (dbError) {
      error = dbError;
    } else {
      data = dbData;
    }
    return {
      ...userInfo,
      ...data,
      uid: userAuth.uid,
      error,
    };
  }, []);

  const signOut = useCallback(async () => {
    clearGa4UserId();
    await auth.signOut();

    setUser(null);
    setUserData(null);
  }, []);

  const handleSetUserData = async () => {
    if (!user) {
      return;
    }

    const userDataResult = await fetchUserData(user);
    setUserData(userDataResult);
    updateCrossDomainCookie(userDataResult);
  };

  const getAndSetUserData = useCallback(async () => {
    if (!user) {
      return;
    }

    setIsLoading(true);
    await handleSetUserData();
    setIsLoading(false);
  }, [user]);

  const clearVerifyEmailError = useCallback(() => setVerifyEmailError(null), []);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    user,
    isLoading,
    userData,
    handleSetUserData,
    signOut,
    pauseAuthListener,
    resumeAuthListener,
    verifyEmailError,
    clearVerifyEmailError,
  };

  useEffect(() => {
    if (user) {
      getAndSetUserData();
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (userAuth) => {
      if (authListenerPausedRef.current) {
        return;
      }

      // A phone-only auth state (from signInWithPhoneNumber/confirm) is always an
      // intermediate step in MFA enrollment. If this tab's listener is not paused,
      // this auth change originated from another tab's phone OTP flow — skip it
      // to avoid corrupting this tab's session state.
      const isPhoneOnlyUser = userAuth &&
        userAuth.providerData?.length === 1 &&
        userAuth.providerData[0]?.providerId === 'phone';

      if (isPhoneOnlyUser) {
        setIsLoading(false);
        return;
      }

      callbackIdRef.current += 1;
      const currentCallbackId = callbackIdRef.current;

      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');

      if (mode === 'verifyEmail' && oobCode) {
        try {
          // Apply the email verification code
          await auth.applyActionCode(oobCode);
          // If user is signed in, reload to get updated emailVerified status
          if (userAuth) {
            await userAuth.reload();
            // Force token refresh to get updated claims
            await userAuth.getIdToken(true);
            // Mark token as refreshed so next API call includes the header
            markTokenAsRefreshed();
          }
        } catch (error) {
          // Determine user-friendly error message
          let errorMessage = 'Email verification failed.';
          if (error.code === 'auth/invalid-action-code' || error.code === 'auth/expired-action-code') {
            errorMessage = 'This verification link has expired or has already been used. Please request a new verification email.';
          } else if (error.code === 'auth/user-disabled') {
            errorMessage = 'This account has been disabled. Please contact support.';
          } else if (error.message) {
            errorMessage = `Email verification failed: ${error.message}`;
          }
          console.error('Error applying verification code:', error, errorMessage);
          setVerifyEmailError(errorMessage);
        } finally {
          const cleanParams = new URLSearchParams(window.location.search);
          cleanParams.delete('mode');
          cleanParams.delete('oobCode');
          const cleanSearch = cleanParams.toString();
          const cleanUrl = window.location.pathname
            + (cleanSearch ? `?${cleanSearch}` : '')
            + window.location.hash;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }

      // Only update state if this callback is still the latest one
      if (currentCallbackId === callbackIdRef.current) {
        if (userAuth) {
          safeStorage.setItem('loggedIn', true);
          markTokenAsRefreshed();
          identifyImpactUser(userAuth);
          setUser(userAuth);
          setIsLoading(false);
        } else {
          safeStorage.setItem('loggedIn', false);
          safeStorage.removeItem('logged_into_closet_cash');
          setUser(null);
          setIsLoading(false);
        }
      }
    });
    return unsubscribe;
  }, []);

  return (
    <FirebaseAuthContext.Provider value={value}>
      {children}
    </FirebaseAuthContext.Provider>
  );
};
